import { useState } from "react";
import { Receipt } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePicker } from "@/components/ui/date-picker";
import { SegmentedFilter } from "@/components/ui/segmented-filter";
import { Dialog, DialogHeader, DialogBody, DialogFooter } from "@/components/ui/dialog";
import { AdminDialogHeading } from "@/components/admin/AdminDialogHeading";
import { COMPANY_BILLING_MODEL_LABEL } from "@/lib/status-configs";
import type { Company, CompanyBillingModel } from "@/types";

export type BillingTermsPatch = Pick<
  Company,
  | "billingModel"
  | "cofundingPercent"
  | "discountPercent"
  | "contractExpiresAt"
  | "creditExpiresAt"
  | "paymentTermDays"
  | "legalName"
  | "address"
  | "city"
  | "taxId"
  | "vatId"
>;

const MODEL_OPTIONS = (["credit", "invoice"] as const).map((v) => ({ value: v, label: COMPANY_BILLING_MODEL_LABEL[v] }));

function clampInt(value: string, min: number, max: number, fallback: number): number {
  const n = Math.round(Number(value));
  return Number.isFinite(n) ? Math.min(max, Math.max(min, n)) : fallback;
}

export function BillingTermsDialog({
  company,
  isPending,
  onSubmit,
  onClose,
}: {
  company: Company;
  isPending: boolean;
  onSubmit: (patch: BillingTermsPatch) => void;
  onClose: () => void;
}) {
  const [billingModel, setBillingModel] = useState<CompanyBillingModel>(company.billingModel);
  const [cofunding, setCofunding] = useState(String(company.cofundingPercent));
  const [discount, setDiscount] = useState(String(company.discountPercent));
  const [contractExpiresAt, setContractExpiresAt] = useState(company.contractExpiresAt ?? "");
  const [creditExpiresAt, setCreditExpiresAt] = useState(company.creditExpiresAt ?? "");
  const [paymentTermDays, setPaymentTermDays] = useState(String(company.paymentTermDays));
  const [legalName, setLegalName] = useState(company.legalName ?? "");
  const [address, setAddress] = useState(company.address ?? "");
  const [city, setCity] = useState(company.city ?? "");
  const [taxId, setTaxId] = useState(company.taxId ?? "");
  const [vatId, setVatId] = useState(company.vatId ?? "");

  const text = (v: string) => (v.trim() ? v.trim() : null);

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    onSubmit({
      billingModel,
      cofundingPercent: clampInt(cofunding, 1, 100, 100),
      discountPercent: clampInt(discount, 0, 100, 0),
      contractExpiresAt: contractExpiresAt || null,
      creditExpiresAt: creditExpiresAt || null,
      paymentTermDays: clampInt(paymentTermDays, 0, 120, 15),
      legalName: text(legalName),
      address: text(address),
      city: text(city),
      taxId: text(taxId),
      vatId: text(vatId),
    });
  };

  return (
    <Dialog open onClose={onClose}>
      <form onSubmit={submit}>
        <DialogHeader onClose={onClose}>
          <AdminDialogHeading
            icon={Receipt}
            title="Uslovi naplate"
            description="Kako firma plaća seanse svojih zaposlenih."
          />
        </DialogHeader>
        <DialogBody>
          <div className="flex flex-col gap-5">
            <div className="space-y-2">
              <Label>Model naplate</Label>
              <SegmentedFilter value={billingModel} onChange={setBillingModel} options={MODEL_OPTIONS} />
              <p className="text-xs text-muted-foreground">
                {billingModel === "credit"
                  ? "Seanse se skidaju sa unaprijed uplaćenog kredita."
                  : "Zaposleni zakazuju bez kredita, firma na kraju mjeseca dobija jednu fakturu."}
              </p>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="bt-cofunding">Firma plaća (%)</Label>
                <Input id="bt-cofunding" type="number" min={1} max={100} step={1} value={cofunding} onChange={(e) => setCofunding(e.target.value)} />
                <p className="text-xs text-muted-foreground">100 = cijela seansa. Ispod 100 zaposleni plaća ostatak karticom ili virmanom.</p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="bt-discount">Rabat (%)</Label>
                <Input id="bt-discount" type="number" min={0} max={100} step={1} value={discount} onChange={(e) => setDiscount(e.target.value)} />
                <p className="text-xs text-muted-foreground">Ide samo iz provizije platforme, terapeut uvijek dobija svoj puni dio.</p>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Ugovor ističe</Label>
                <DatePicker value={contractExpiresAt} onChange={setContractExpiresAt} placeholder="Bez isteka" />
              </div>
              <div className="space-y-2">
                <Label>Kredit ističe</Label>
                <DatePicker value={creditExpiresAt} onChange={setCreditExpiresAt} placeholder="Bez isteka" />
              </div>
            </div>
            <p className="-mt-3 text-xs text-muted-foreground">
              Istek samo šalje podsjetnik supervizoru i adminu 30 i 7 dana prije; zakazivanje se ne blokira.
              {(contractExpiresAt || creditExpiresAt) && (
                <>
                  {" "}
                  <button
                    type="button"
                    className="underline underline-offset-2 hover:text-foreground"
                    onClick={() => {
                      setContractExpiresAt("");
                      setCreditExpiresAt("");
                    }}
                  >
                    Ukloni datume
                  </button>
                </>
              )}
            </p>

            <div className="space-y-3 border-t border-border/60 pt-4">
              <p className="text-sm font-semibold text-foreground">Podaci za fakturu i predračun</p>
              <div className="space-y-2">
                <Label htmlFor="bt-legal">Pravni naziv</Label>
                <Input id="bt-legal" value={legalName} onChange={(e) => setLegalName(e.target.value)} placeholder="npr. TechFirma d.o.o." maxLength={200} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bt-address">Adresa</Label>
                  <Input id="bt-address" value={address} onChange={(e) => setAddress(e.target.value)} maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bt-city">Grad</Label>
                  <Input id="bt-city" value={city} onChange={(e) => setCity(e.target.value)} placeholder="npr. 71000 Sarajevo" maxLength={200} />
                </div>
              </div>
              <div className="grid grid-cols-3 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="bt-tax">ID broj</Label>
                  <Input id="bt-tax" value={taxId} onChange={(e) => setTaxId(e.target.value)} maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bt-vat">PDV broj</Label>
                  <Input id="bt-vat" value={vatId} onChange={(e) => setVatId(e.target.value)} placeholder="opciono" maxLength={200} />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bt-term">Rok plaćanja (dana)</Label>
                  <Input id="bt-term" type="number" min={0} max={120} step={1} value={paymentTermDays} onChange={(e) => setPaymentTermDays(e.target.value)} />
                </div>
              </div>
            </div>
          </div>
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Otkaži
          </Button>
          <Button type="submit" disabled={isPending}>
            {isPending ? "Spremanje..." : "Spremi uslove"}
          </Button>
        </DialogFooter>
      </form>
    </Dialog>
  );
}
